import { useCallback, useMemo } from 'react';
import { selector, useRecoilValue, useSetRecoilState } from 'recoil';
import { groupSettingsLoadedState, groupSettingsState } from '../../store/groupSettingsAtoms';
import { CustomField, EmailFolder, EmailTypeSettings, GroupSettings, TagGroup } from '../../types/common';
import { DashboardColumn, InvestmentThesis, Workspace } from '../../types/files';

const groupSettingsSelector = selector({
  key: 'groupSettingsSelector',
  get: ({ get }) => {
    const settingsLoaded = get(groupSettingsLoadedState);
    const settings = get(groupSettingsState);

    return {
      settingsLoaded,
      settings,
      group: settings?.group ?? '',
      defaultStatus: settings?.defaultStatus ?? '',
      statuses: settings?.statuses?.map(status => status ? status.trim().toLowerCase() : '<rename this statuses>') ?? [],
      investmentStages: [...(settings?.investmentStages ?? [])].sort((a, b) => (a > b ? 1 : 0)),
      investmentThesis: settings?.investmentThesis ?? null,
      tags: [...(settings?.tags ?? [])].sort((a, b) => (a > b ? 1 : 0)),
      keyPeopleTags: settings?.keyPeopleTags ?? [],
      tagGroups: settings?.tagGroups ?? [],
      customFields: settings?.customFields ?? [],
      shouldSyncAllDashboardsExternally: settings?.shouldSyncAllDashboardsExternally ?? false,
      shouldIngestDashboardsFromEmail: settings?.shouldIngestDashboardsFromEmail ?? false,
      shouldSendDigestEmail: settings?.shouldSendDigestEmail ?? false,
      shouldSendIngestionNotifications: settings?.shouldSendIngestionNotifications ?? false,
      groupWorkspaces: settings?.workspaces ?? [],
      excludedCompanyTerms: settings?.excludedCompanyTerms ?? [],
      dashboardColumns: settings?.dashboardColumns ?? [],
      emailFolders: settings?.emailFolders ?? [],
      emailTypeSettings: settings?.emailTypeSettings ?? { newDeal: true, companyUpdate: true, newFundOpportunity: true, sharedPipeline: true },
      connectedApps: settings?.connectedApps ?? [],
    };
  },
});

const useGroupSettingsContext = () => {
  const setGroupSettingsLoaded = useSetRecoilState(groupSettingsLoadedState);
  const setGroupSettings = useSetRecoilState(groupSettingsState);
  const {
    settingsLoaded,
    settings,
    group,
    defaultStatus,
    statuses,
    investmentStages,
    investmentThesis,
    tags,
    keyPeopleTags,
    tagGroups,
    customFields,
    shouldSyncAllDashboardsExternally,
    shouldIngestDashboardsFromEmail,
    shouldSendDigestEmail,
    shouldSendIngestionNotifications,
    groupWorkspaces,
    excludedCompanyTerms,
    dashboardColumns,
    emailFolders,
    emailTypeSettings,
    connectedApps,
  } = useRecoilValue(groupSettingsSelector);

  const setDefaultStatus = useCallback((defaultStatus: string | ((prev: string) => string)) =>
    setGroupSettings(typeof defaultStatus === "function"
      ? (prev => ({ ...prev as GroupSettings, defaultStatus: defaultStatus(prev?.defaultStatus || '')?.trim() }))
      : (prev => ({ ...prev as GroupSettings, defaultStatus: defaultStatus?.trim() })))
    , [setGroupSettings]);

  const setStatuses = useCallback((statuses: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof statuses === "function"
      ? (prev => ({ ...prev as GroupSettings, statuses: statuses(prev?.statuses ?? []).map(status => status?.trim()) }))
      : (prev => ({ ...prev as GroupSettings, statuses: statuses.map(status => status?.trim()) })))
    , [setGroupSettings]);

  const setTags = useCallback((tags: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof tags === "function"
      ? (prev => ({ ...prev as GroupSettings, tags: tags(prev?.tags ?? []) }))
      : (prev => ({ ...prev as GroupSettings, tags })))
    , [setGroupSettings]);

  const setKeyPeopleTags = useCallback((keyPeopleTags: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof keyPeopleTags === "function"
      ? (prev => ({ ...prev as GroupSettings, keyPeopleTags: keyPeopleTags(prev?.keyPeopleTags ?? []) }))
      : (prev => ({ ...prev as GroupSettings, keyPeopleTags })))
    , [setGroupSettings]);

  const setTagGroups = useCallback((tagGroups: TagGroup[] | ((prev: TagGroup[]) => TagGroup[])) =>
    setGroupSettings(typeof tagGroups === "function"
      ? (prev => ({ ...prev as GroupSettings, tagGroups: tagGroups(prev?.tagGroups ?? []) }))
      : (prev => ({ ...prev as GroupSettings, tagGroups })))
    , [setGroupSettings]);

  const setCustomFields = useCallback((customFields: CustomField[] | ((prev: CustomField[]) => CustomField[])) =>
    setGroupSettings(typeof customFields === "function"
      ? (prev => ({ ...prev as GroupSettings, customFields: customFields(prev?.customFields ?? []) }))
      : (prev => ({ ...prev as GroupSettings, customFields })))
    , [setGroupSettings]);

  const setStages = useCallback((stages: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof stages === "function"
      ? (prev => ({ ...prev as GroupSettings, investmentStages: stages(prev?.investmentStages ?? []) }))
      : (prev => ({ ...prev as GroupSettings, investmentStages: stages })))
    , [setGroupSettings]);

  const setExternalSync = useCallback((externalSync: boolean | ((prev: boolean) => boolean)) =>
    setGroupSettings(typeof externalSync === "function"
      ? (prev => ({ ...prev as GroupSettings, shouldSyncAllDashboardsExternally: externalSync(prev?.shouldSyncAllDashboardsExternally || false) }))
      : (prev => ({ ...prev as GroupSettings, shouldSyncAllDashboardsExternally: externalSync })))
    , [setGroupSettings]);

  const setAutoIngestFromEmail = useCallback((autoIngestFromEmail: boolean | ((prev: boolean) => boolean)) =>
    setGroupSettings(typeof autoIngestFromEmail === "function"
      ? (prev => ({ ...prev as GroupSettings, shouldIngestDashboardsFromEmail: autoIngestFromEmail(prev?.shouldIngestDashboardsFromEmail || false) }))
      : (prev => ({ ...prev as GroupSettings, shouldIngestDashboardsFromEmail: autoIngestFromEmail })))
    , [setGroupSettings]);

  const setAutoSendDigestEmail = useCallback((autoSendDigestEmail: boolean | ((prev: boolean) => boolean)) =>
    setGroupSettings(typeof autoSendDigestEmail === "function"
      ? (prev => ({ ...prev as GroupSettings, shouldSendDigestEmail: autoSendDigestEmail(prev?.shouldSendDigestEmail || false) }))
      : (prev => ({ ...prev as GroupSettings, shouldSendDigestEmail: autoSendDigestEmail })))
    , [setGroupSettings]);

  const setAutoSendIngestionNotification = useCallback((autoSendIngestionNotifications: boolean | ((prev: boolean) => boolean)) =>
    setGroupSettings(typeof autoSendIngestionNotifications === "function"
      ? (prev => ({ ...prev as GroupSettings, shouldSendIngestionNotifications: autoSendIngestionNotifications(prev?.shouldSendIngestionNotifications || false) }))
      : (prev => ({ ...prev as GroupSettings, shouldSendIngestionNotifications: autoSendIngestionNotifications })))
    , [setGroupSettings]);

  const setInvestmentThesis = useCallback((investmentThesis: InvestmentThesis | null | ((prev?: InvestmentThesis | null) => InvestmentThesis | null)) =>
    setGroupSettings(typeof investmentThesis === "function"
      ? (prev => ({ ...prev as GroupSettings, investmentThesis: investmentThesis(prev?.investmentThesis) ?? undefined }))
      : (prev => ({ ...prev as GroupSettings, investmentThesis: investmentThesis ?? undefined })))
    , [setGroupSettings]);

  const setGroupWorkspaces = useCallback((workspaces: Workspace[] | ((prev: Workspace[]) => Workspace[])) =>
    setGroupSettings(typeof workspaces === "function"
      ? (prev => ({ ...prev as GroupSettings, workspaces: workspaces(prev?.workspaces ?? []) }))
      : (prev => ({ ...prev as GroupSettings, workspaces })))
    , [setGroupSettings]);

  const setExcludedCompanyTerms = useCallback((excludedCompanyTerms: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof excludedCompanyTerms === "function"
      ? (prev => ({ ...prev as GroupSettings, excludedCompanyTerms: excludedCompanyTerms(prev?.excludedCompanyTerms ?? []) }))
      : (prev => ({ ...prev as GroupSettings, excludedCompanyTerms })))
    , [setGroupSettings]);

  const setDashboardColumns = useCallback((dashboardColumns: DashboardColumn[] | ((prev: DashboardColumn[]) => DashboardColumn[])) =>
    setGroupSettings(typeof dashboardColumns === "function"
      ? (prev => ({ ...prev as GroupSettings, dashboardColumns: dashboardColumns(prev?.dashboardColumns ?? []) }))
      : (prev => ({ ...prev as GroupSettings, dashboardColumns })))
    , [setGroupSettings]);

  const setEmailFolders = useCallback((emailFolders: EmailFolder[] | ((prev: EmailFolder[]) => EmailFolder[])) =>
    setGroupSettings(typeof emailFolders === "function"
      ? (prev => ({ ...prev as GroupSettings, emailFolders: emailFolders(prev?.emailFolders ?? []) }))
      : (prev => ({ ...prev as GroupSettings, emailFolders })))
    , [setGroupSettings]);

  const setEmailTypeSettings = useCallback((emailTypeSettings: EmailTypeSettings | ((prev: EmailTypeSettings) => EmailTypeSettings)) =>
    setGroupSettings(typeof emailTypeSettings === "function"
      ? (prev => ({ ...prev as GroupSettings, emailTypeSettings: emailTypeSettings(prev?.emailTypeSettings ?? { newDeal: true, companyUpdate: true, newFundOpportunity: true, sharedPipeline: true }) }))
      : (prev => ({ ...prev as GroupSettings, emailTypeSettings })))
    , [setGroupSettings]);

  const setConnectedApps = useCallback((connectedApps: string[] | ((prev: string[]) => string[])) =>
    setGroupSettings(typeof connectedApps === "function"
      ? (prev => ({ ...prev as GroupSettings, connectedApps: connectedApps(prev?.connectedApps ?? []) }))
      : (prev => ({ ...prev as GroupSettings, connectedApps })))
    , [setGroupSettings]);

  const context = useMemo(() => ({
    settingsLoaded,
    settings,
    group,
    defaultStatus,
    statuses,
    tags,
    keyPeopleTags,
    tagGroups,
    customFields,
    stages: investmentStages,
    externalSync: shouldSyncAllDashboardsExternally,
    autoIngestFromEmail: shouldIngestDashboardsFromEmail,
    autoSendDigestEmail: shouldSendDigestEmail,
    autoSendIngestionNotifications: shouldSendIngestionNotifications,
    investmentThesis,
    groupWorkspaces,
    excludedCompanyTerms,
    dashboardColumns,
    emailFolders,
    emailTypeSettings,
    connectedApps,
    setGroupSettingsLoaded,
    setGroupSettings,
    setDefaultStatus,
    setStatuses,
    setTags,
    setKeyPeopleTags,
    setTagGroups,
    setCustomFields,
    setStages,
    setExternalSync,
    setAutoIngestFromEmail,
    setAutoSendDigestEmail,
    setAutoSendIngestionNotification,
    setInvestmentThesis,
    setGroupWorkspaces,
    setExcludedCompanyTerms,
    setDashboardColumns,
    setEmailFolders,
    setEmailTypeSettings,
    setConnectedApps,
  }), [
    settingsLoaded,
    settings,
    group,
    defaultStatus,
    statuses,
    tags,
    keyPeopleTags,
    tagGroups,
    customFields,
    investmentStages,
    shouldSyncAllDashboardsExternally,
    shouldIngestDashboardsFromEmail,
    shouldSendDigestEmail,
    shouldSendIngestionNotifications,
    investmentThesis,
    groupWorkspaces,
    excludedCompanyTerms,
    dashboardColumns,
    emailFolders,
    emailTypeSettings,
    connectedApps,
    setGroupSettingsLoaded,
    setGroupSettings,
    setDefaultStatus,
    setStatuses,
    setTags,
    setKeyPeopleTags,
    setTagGroups,
    setCustomFields,
    setStages,
    setExternalSync,
    setAutoIngestFromEmail,
    setAutoSendDigestEmail,
    setAutoSendIngestionNotification,
    setInvestmentThesis,
    setGroupWorkspaces,
    setExcludedCompanyTerms,
    setDashboardColumns,
    setEmailFolders,
    setEmailTypeSettings,
    setConnectedApps,
  ]);

  return context;
};

export default useGroupSettingsContext;